import React from 'react';
import { generateDriverColors } from './mapZones';
import { formatTripStatus } from './mapMarkerHtml';

export function MarkerPopupBody({ marker, formatTime }) {
  return (
    <div className="min-w-[200px] text-sm text-gray-800">
      <div className="font-semibold text-gray-900">{marker.label}</div>
      {marker.isBusyTrip && marker.tripStatus && (
        <div className="text-amber-600 font-medium mt-1">
          Status: {formatTripStatus(marker.tripStatus)}
        </div>
      )}
      {marker.clientName && (
        <div className="text-gray-600 mt-1">
          <strong>Client:</strong> {marker.clientName}
        </div>
      )}
      {marker.type === 'combined' && marker.nextClientName && (
        <div className="text-gray-600 mt-1">
          <strong>Next Client:</strong> {marker.nextClientName}
        </div>
      )}
      {marker.address && (
        <div className="text-gray-600 mt-1">
          <strong>Address:</strong> {marker.address}
        </div>
      )}
      {marker.scheduledTime && (
        <div className="text-gray-600 mt-1">
          <strong>Scheduled:</strong> {formatTime(marker.scheduledTime)}
        </div>
      )}
      {marker.appointmentTime && (
        <div className="text-gray-600 mt-1">
          <strong>Appointment:</strong> {formatTime(marker.appointmentTime)}
        </div>
      )}
      {marker.eta != null && marker.eta !== '' && (
        <div className="font-medium text-blue-600 mt-2">
          {marker.isBusyTrip ? 'Available in:' : 'ETA to pickup:'} {Math.round(marker.eta)} min
        </div>
      )}
    </div>
  );
}

export function buildZoneViewMarkers(driversWithTrips, visibleDriverZones) {
  const allMarkers = [];
  const driverColors = generateDriverColors(driversWithTrips.length);

  driversWithTrips
    .filter((driver) => visibleDriverZones.includes(driver.driver_id))
    .forEach((driver, driverIndex) => {
      if (!driver.trips?.length) return;

      const sortedTrips = [...driver.trips].sort(
        (a, b) => (a.sequence ?? 999) - (b.sequence ?? 999)
      );
      const driverColor = driverColors[driverIndex];
      const isBusyDriver = driver.is_busy === true;
      const driverLat = driver.current_latitude || driver.ref_latitude;
      const driverLng = driver.current_longitude || driver.ref_longitude;

      if (driverLat && driverLng) {
        allMarkers.push({
          type: 'driver',
          position: [driverLat, driverLng],
          label: `${driver.driver_name} - ${isBusyDriver ? 'Current Position' : 'Start'}`,
          driverName: driver.driver_name,
          driverColor,
          isBusy: isBusyDriver,
          profileImageURL: driver.profileImageURL || '',
        });
      }

      let busyTripCount = 0;
      sortedTrips.forEach((trip) => {
        if (trip.is_busy_trip || trip.sequence === 0) busyTripCount++;
      });

      let prevDropoff = null;
      let currentBusyIndex = 0;
      let currentPlannedIndex = 0;

      sortedTrips.forEach((trip) => {
        const isBusyTrip = trip.is_busy_trip || trip.sequence === 0;
        let displayNumber;
        let tripLabel;
        if (isBusyTrip) {
          currentBusyIndex++;
          displayNumber = busyTripCount > 1 ? `B${currentBusyIndex}` : 'B';
          tripLabel = `Current Trip ${currentBusyIndex}`;
        } else {
          currentPlannedIndex++;
          displayNumber = currentPlannedIndex;
          tripLabel = `Trip ${currentPlannedIndex}`;
        }

        const etaValue = isBusyTrip ? trip.available_in_minutes : trip.eta_to_pickup_minutes;
        const pickupLat = trip.pickup_lat || trip.origin?.latitude;
        const pickupLng = trip.pickup_lng || trip.origin?.longitude;
        const dropoffLat = trip.dropoff_lat || trip.destination?.latitude;
        const dropoffLng = trip.dropoff_lng || trip.destination?.longitude;

        const isSameLocation = (lat1, lng1, lat2, lng2) => {
          if (!lat1 || !lng1 || !lat2 || !lng2) return false;
          const tolerance = 0.0005;
          return Math.abs(lat1 - lat2) < tolerance && Math.abs(lng1 - lng2) < tolerance;
        };

        const overlapsWithPrevDropoff =
          prevDropoff && isSameLocation(pickupLat, pickupLng, prevDropoff.lat, prevDropoff.lng);

        if (pickupLat && pickupLng && !overlapsWithPrevDropoff) {
          allMarkers.push({
            type: 'pickup',
            position: [pickupLat, pickupLng],
            label: `${driver.driver_name} - ${tripLabel} Pickup`,
            tripNumber: displayNumber,
            driverName: driver.driver_name,
            driverColor,
            isBusyTrip,
            clientName: trip.client_name,
            address: trip.pickup_address,
            scheduledTime: trip.schedule_time,
            eta: etaValue,
            tripStatus: trip.trip_status,
          });
        }

        if (overlapsWithPrevDropoff && allMarkers.length > 0) {
          const lastMarker = allMarkers[allMarkers.length - 1];
          if (lastMarker.type === 'dropoff') {
            lastMarker.type = 'combined';
            lastMarker.label = `${lastMarker.driverName} - ${lastMarker.label.split(' - ')[1]} → ${tripLabel} Pickup`;
            lastMarker.nextPickupNumber = displayNumber;
            lastMarker.nextClientName = trip.client_name;
            lastMarker.nextTripStatus = trip.trip_status;
            lastMarker.driverColor = driverColor;
          }
        }

        if (dropoffLat && dropoffLng) {
          allMarkers.push({
            type: 'dropoff',
            position: [dropoffLat, dropoffLng],
            label: `${driver.driver_name} - ${tripLabel} Dropoff`,
            tripNumber: displayNumber,
            driverName: driver.driver_name,
            driverColor,
            isBusyTrip,
            clientName: trip.client_name,
            address: trip.dropoff_address,
            appointmentTime: trip.appointment_time,
            tripStatus: trip.trip_status,
          });
          prevDropoff = { lat: dropoffLat, lng: dropoffLng };
        } else {
          prevDropoff = null;
        }
      });
    });

  return allMarkers;
}

export function htmlToLeafletIcon(markerHtml) {
  if (!markerHtml?.html) return undefined;
  return {
    html: markerHtml.html,
    width: markerHtml.width,
    height: markerHtml.height,
  };
}
